import {
  BarChart3,
  BookOpen,
  Building2,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Factory,
  Home,
  LayoutDashboard,
  ListChecks,
  Megaphone,
  Plug,
  QrCode,
  ScrollText,
  Settings,
  SlidersHorizontal,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react'
import { moduleEnabled } from '@/lib/modules'
import { can, type Capability } from '@/lib/plantAccess'
import type { TranslationKey } from '@/i18n/dictionary'
import type { ModuleName, User } from '@/types/api'

/*
 * What the navigation offers, and to whom — kept apart from the components
 * that draw it.
 *
 * Three surfaces read this: the sidebar, the phone's bottom bar and the search
 * palette. They used to be one array inside AppShell filtered in one place,
 * and the admin sub-pages were a second array inside AdminLayout filtered in
 * another. With the admin pages moving into the sidebar *and* into search, a
 * third copy of either rule would be the one that drifts — the palette
 * offering a page the sidebar hides, which the route guard then refuses.
 *
 * Plain data and plain functions, so the rules are tested without rendering
 * anything (navigation.test.ts).
 */

export interface NavItem {
  to: string
  /**
   * Translation key, not a label.
   *
   * These are module-level constants, so there is no hook available where they
   * are declared — the components resolve them at render. That is also what
   * makes the sidebar relabel itself on a language switch rather than keeping
   * whatever language it was first mounted in.
   */
  labelKey: TranslationKey
  icon: LucideIcon
  /**
   * What the destination is for, shown as the bottom bar's tooltip.
   *
   * Not a repeat of `label` — the label is already printed under the icon
   * there, so echoing it would be noise. The collapsed sidebar is the opposite
   * case: the label *is* the missing information, so its tooltip uses `label`.
   */
  hintKey: TranslationKey
  managerOnly?: boolean
  adminOnly?: boolean
  /** Platform owner only — the one destination that crosses customers. */
  ownerOnly?: boolean
  /** Hidden when the company has this module switched off. */
  module?: ModuleName
  /** One of the four tabs in the phone's bottom bar. */
  tab?: boolean
}

export interface NavGroup {
  id: 'work' | 'overview'
  labelKey: TranslationKey
  items: NavItem[]
}

/*
 * The groups, as the user asked for them: the daily work, then the overview.
 *
 * "Rad" is everything somebody does during a shift and is the same four the
 * bottom bar carries, so the phone and the desk agree about what the core of
 * the application is.
 */
const WORK: NavItem[] = [
  { to: '/', labelKey: 'nav.home', icon: Home, hintKey: 'nav.home.hint', tab: true },
  {
    to: '/prijave',
    labelKey: 'nav.submissions',
    icon: ListChecks,
    hintKey: 'nav.submissions.hint',
    module: 'submissions',
    tab: true,
  },
  {
    to: '/zadaci',
    labelKey: 'nav.tasks',
    icon: ClipboardList,
    hintKey: 'nav.tasks.hint',
    module: 'submissions',
    tab: true,
  },
  {
    to: '/5s',
    labelKey: 'nav.fiveS',
    icon: ClipboardCheck,
    hintKey: 'nav.fiveS.hint',
    module: 'fiveS',
    tab: true,
  },
]

const OVERVIEW: NavItem[] = [
  {
    to: '/analitika',
    labelKey: 'nav.analytics',
    icon: BarChart3,
    hintKey: 'nav.analytics.hint',
    managerOnly: true,
    module: 'analytics',
  },
]

/** The section itself — a heading in the sidebar that opens onto its pages. */
export const ADMIN_SECTION: NavItem = {
  to: '/admin',
  labelKey: 'nav.admin',
  icon: Settings,
  hintKey: 'nav.admin.hint',
  adminOnly: true,
}

/*
 * The bottom of the rail: read rarely, needed when it is.
 */
const FOOTER: NavItem[] = [
  {
    to: '/vlasnik',
    /*
     * The way back, not the way in.
     *
     * An owner lands in their own console and never normally sees this shell —
     * `HomeForRole` redirects them. This exists for the case where one deep
     * links into a customer screen and needs an exit that is not the browser's
     * back button.
     */
    labelKey: 'nav.owner',
    icon: Building2,
    hintKey: 'nav.owner.hint',
    ownerOnly: true,
  },
  {
    /*
     * Nobody navigates here during a shift — it is read once when an account is
     * new and then when something refuses to work. So it sits at the foot of
     * the rail rather than among the daily destinations, and the bottom bar
     * does not spend one of its four slots on it.
     */
    to: '/uputstvo',
    labelKey: 'nav.guide',
    icon: BookOpen,
    hintKey: 'nav.guide.hint',
  },
]

export interface AdminPage {
  to: string
  labelKey: TranslationKey
  icon: LucideIcon
  module?: ModuleName
  capability?: Capability
  /**
   * Current on its own path only. The overview sits at the section's root, so
   * by whole segments it would be "current" on every admin page at once.
   */
  end?: boolean
}

/*
 * The admin pages. The paths stay Bosnian and are not translated: they are
 * bookmarked, pasted into chats and printed on stickers, so a URL that moved
 * with the language switcher would break every one of those the moment
 * somebody changed it.
 *
 * Moved here from AdminLayout's tab row, rules and all.
 *
 * Two belong to a module: the 5S checklists to 5S, the integration to the MES.
 * A company without the module does not get the page — the server refuses
 * those routes anyway, and an entry that opens onto "not switched on" is an
 * entry nobody needed.
 *
 * Two belong to the glavni administrator: the integration and the log. The
 * server refuses both to anybody else outright, reads included, so they go
 * the same way. Postavke stays for every administrator — it is read-only for
 * the others, and it is where they find out who the glavni administrator is.
 */
export const ADMIN_PAGES: readonly AdminPage[] = [
  /*
   * First, and every administrator's: the section opens on what needs doing
   * rather than on the first list in it. Each of its lines links into one of
   * the pages below, so it needs no module or capability of its own — the
   * server leaves out what the reader may not see.
   */
  { to: '/admin', labelKey: 'admin.nav.overview', icon: LayoutDashboard, end: true },
  { to: '/admin/korisnici', labelKey: 'admin.nav.users', icon: Users },
  { to: '/admin/pogoni', labelKey: 'admin.nav.plants', icon: Factory },
  { to: '/admin/radni-centri', labelKey: 'admin.nav.workCenters', icon: Wrench },
  { to: '/admin/smjene', labelKey: 'admin.nav.shifts', icon: Clock },
  { to: '/admin/obrasci', labelKey: 'admin.nav.templates', icon: ClipboardCheck, module: 'fiveS' },
  { to: '/admin/nalepnice', labelKey: 'admin.nav.badges', icon: QrCode },
  {
    to: '/admin/integracija',
    labelKey: 'admin.nav.integration',
    icon: Plug,
    module: 'mes',
    capability: 'manageCompany',
  },
  { to: '/admin/postavke', labelKey: 'admin.nav.settings', icon: SlidersHorizontal },
  { to: '/admin/dnevnik', labelKey: 'admin.nav.log', icon: ScrollText, capability: 'manageCompany' },
]

function allowed(item: NavItem, user: User | null, impersonating: boolean): boolean {
  const modules = user?.company?.modules

  return (
    (!item.managerOnly || Boolean(user?.isManager)) &&
    (!item.adminOnly || user?.role === 'ADMIN') &&
    /*
     * Hidden during a support session as well as from customers.
     *
     * The session runs as the customer's user, so `isOwner` is already false
     * and this is belt-and-braces — but the server refuses the panel from
     * inside a support session for a reason worth repeating in the UI: the
     * cross-customer view has no business being on a screen the customer's
     * own administrator might be watching.
     */
    (!item.ownerOnly || (Boolean(user?.isOwner) && !impersonating)) &&
    // A module the company does not have is not offered at all — a
    // destination that only says "not switched on" is not a destination.
    (!item.module || moduleEnabled(modules, item.module))
  )
}

/** The admin pages this person may open, in the sidebar's order. */
export function visibleAdminPages(user: User | null): AdminPage[] {
  if (user?.role !== 'ADMIN') return []

  return ADMIN_PAGES.filter(
    (page) =>
      (!page.module || moduleEnabled(user.company?.modules, page.module)) &&
      (!page.capability || can(user, page.capability)),
  )
}

export interface VisibleNavigation {
  /** Only the groups with something in them. */
  groups: NavGroup[]
  /** Empty for anybody who is not an administrator. */
  admin: AdminPage[]
  footer: NavItem[]
  /** The phone's bottom bar. */
  tabs: NavItem[]
}

export function visibleNavigation(user: User | null, impersonating = false): VisibleNavigation {
  const keep = (items: NavItem[]) => items.filter((item) => allowed(item, user, impersonating))

  const groups: NavGroup[] = [
    { id: 'work' as const, labelKey: 'nav.group.work' as const, items: keep(WORK) },
    { id: 'overview' as const, labelKey: 'nav.group.overview' as const, items: keep(OVERVIEW) },
  ].filter((group) => group.items.length > 0)

  return {
    groups,
    admin: allowed(ADMIN_SECTION, user, impersonating) ? visibleAdminPages(user) : [],
    footer: keep(FOOTER),
    tabs: keep(WORK).filter((item) => item.tab),
  }
}

/**
 * Whether a destination is the one on screen.
 *
 * Whole segments, so `/5s` is current on `/5s/nova` but a later `/5sx` would
 * not be, and `/` only ever on the home screen itself.
 */
export function isActivePath(to: string, pathname: string): boolean {
  if (to === '/') return pathname === '/'

  return pathname === to || pathname.startsWith(`${to}/`)
}

/** `isActivePath` for an admin page, which may ask to match its own path only. */
export function isAdminPageActive(page: AdminPage, pathname: string): boolean {
  return page.end ? pathname === page.to || pathname === `${page.to}/` : isActivePath(page.to, pathname)
}

// ---------------------------------------------------------------------------
// Where am I
// ---------------------------------------------------------------------------

export interface Crumb {
  labelKey: TranslationKey
  /** Absent on the last crumb, and on a section that is a heading only. */
  to?: string
  /**
   * The words themselves, when they are a record's own name rather than
   * something a dictionary holds — a person's, on their page. Wins over
   * `labelKey`, which stays as what is shown until the name has arrived.
   */
  label?: string
}

/*
 * Names the path cannot know.
 *
 * `/admin/korisnici/01J…` carries an id; the name arrives with the page's own
 * request. The page says it here and the header reads it back. A small store
 * rather than React state, because the header and the page are siblings in the
 * shell and neither renders the other.
 */
const crumbTitles = new Map<string, string>()
const crumbListeners = new Set<() => void>()
let crumbTitlesChanged = 0

/** Name the last crumb of a path, or forget the name with null. */
export function setCrumbTitle(path: string, title: string | null): void {
  if (title) crumbTitles.set(path, title)
  else crumbTitles.delete(path)

  crumbTitlesChanged += 1
  crumbListeners.forEach((listener) => listener())
}

/** For `useSyncExternalStore` in the header. */
export function subscribeCrumbTitles(listener: () => void): () => void {
  crumbListeners.add(listener)

  return () => {
    crumbListeners.delete(listener)
  }
}

/** Changes whenever a name does — the store's snapshot. */
export function crumbTitlesVersion(): number {
  return crumbTitlesChanged
}

/*
 * The header's title, as a breadcrumb.
 *
 * Derived from the path rather than declared by each page, because twenty
 * pages declaring their own would be twenty places to forget it — and because
 * what the header has to answer is "where in the application is this", which
 * is the route's fact, not the page's. A detail screen says which list it
 * belongs to and links back to it, so the way out of a report is in the same
 * place on every screen.
 */
export function breadcrumbFor(pathname: string): Crumb[] {
  const parts = pathname.split('/').filter(Boolean)

  if (parts.length === 0) return [{ labelKey: 'nav.home' }]

  const [first, second, third] = parts

  if (first === 'admin') {
    // One person's page, under the list it was opened from.
    if (second === 'korisnici' && third) {
      const name = crumbTitles.get(pathname)

      return [
        { labelKey: 'nav.admin' },
        { labelKey: 'admin.nav.users', to: '/admin/korisnici' },
        name ? { labelKey: 'crumb.person', label: name } : { labelKey: 'crumb.person' },
      ]
    }

    const page = ADMIN_PAGES.find((candidate) => isAdminPageActive(candidate, pathname))

    return page ? [{ labelKey: 'nav.admin' }, { labelKey: page.labelKey }] : [{ labelKey: 'nav.admin' }]
  }

  if (first === 'prijave') {
    const list: Crumb = { labelKey: 'nav.submissions', to: '/prijave' }

    if (!second) return [{ labelKey: 'nav.submissions' }]
    if (second === 'nova') return [list, { labelKey: 'header.newSubmission' }]

    return third === 'izmjena'
      ? [list, { labelKey: 'crumb.submission', to: `/prijave/${second}` }, { labelKey: 'crumb.edit' }]
      : [list, { labelKey: 'crumb.submission' }]
  }

  if (first === '5s') {
    const list: Crumb = { labelKey: 'nav.fiveS', to: '/5s' }

    if (!second) return [{ labelKey: 'nav.fiveS' }]
    if (second === 'nova') return [list, { labelKey: 'quick.walk' }]

    return third === 'izmjena'
      ? [list, { labelKey: 'crumb.walk', to: `/5s/${second}` }, { labelKey: 'crumb.edit' }]
      : [list, { labelKey: 'crumb.walk' }]
  }

  const single: Record<string, TranslationKey> = {
    zadaci: 'nav.tasks',
    analitika: 'nav.analytics',
    profil: 'account.myAccount',
    uputstvo: 'nav.guide',
    skeniraj: 'header.scanQr',
    obavijesti: 'notifications.title',
  }

  return single[first] ? [{ labelKey: single[first] }] : []
}

// ---------------------------------------------------------------------------
// Starting something
// ---------------------------------------------------------------------------

/** `/zadaci?novi=1` opens the new-task form; read by the tasks screen. */
export const NEW_TASK_PARAM = 'novi'

export interface QuickAction {
  id: 'submission' | 'walk' | 'task'
  to: string
  labelKey: TranslationKey
  hintKey: TranslationKey
  icon: LucideIcon
}

/*
 * What "+ Novo" offers — the two floating buttons, plus the one that was only
 * reachable from the tasks screen.
 *
 * Each is offered only to somebody the server will let through, for the
 * reason the floating 5S button already gave: offering a walk to somebody the
 * API will refuse is worse than not offering it.
 *
 * - A report: anybody, wherever the company has reports.
 * - A 5S walk: whoever may evaluate (`canEvaluate`), where 5S is on.
 * - A task: managers, because only they may assign work. It opens the same
 *   form the tasks screen has — a task always hangs off a report, and that
 *   form asks for the report first.
 */
export function quickActions(user: User | null): QuickAction[] {
  if (!user) return []

  const modules = user.company?.modules
  const actions: QuickAction[] = []

  if (moduleEnabled(modules, 'submissions')) {
    actions.push({
      id: 'submission',
      to: '/prijave/nova',
      labelKey: 'header.newSubmission',
      hintKey: 'quick.submission.hint',
      // A megaphone rather than a plus: this raises something — a fault, a
      // hazard, an idea — and the "+" on the menu already says "new".
      icon: Megaphone,
    })
  }

  if (user.canEvaluate && moduleEnabled(modules, 'fiveS')) {
    actions.push({
      id: 'walk',
      to: '/5s/nova',
      labelKey: 'quick.walk',
      hintKey: 'quick.walk.hint',
      icon: ClipboardCheck,
    })
  }

  if (user.isManager && moduleEnabled(modules, 'submissions')) {
    actions.push({
      id: 'task',
      to: `/zadaci?${NEW_TASK_PARAM}=1`,
      labelKey: 'tasks.new',
      hintKey: 'quick.task.hint',
      icon: ClipboardList,
    })
  }

  return actions
}
